import {
  AddOutlined,
  ContentCopy,
  DeleteOutlineOutlined,
  Edit,
  Power,
  PowerOff,
} from "@mui/icons-material";
import { Box, Button, IconButton } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSysCfgQuery } from "@/api/hooks/sys-cfg-hooks";
import { useCfgPerm, useIsOwnerOrSuperAdmin } from "@/auth/hooks";
import ApplyAuthGroupsTableControl from "@/components/apply-auth-groups-table-control";
import BulkOperationReportModal from "@/components/bulk-operation-report-modal";
import Popconfirm from "@/components/popcofirm";
import { useTableSettings } from "@/hooks/use-table-settings";
import { assembleNamesFilter } from "@/lib/cfg-utils";
import { ROOM_CONFIGS_NAMES } from "@/lib/constants";
import {
  useConfigChangeEnabled,
  useDeleteConfigs,
  useSaveConfig,
  useUpdateConfig,
} from "@/module/room-configs-module/hooks/room-configs-hooks";
import { AiconGameGroupCreateEditModal } from "@/module/service-configs-module/aicon-game-group-module/aicon-game-group-create-edit-modal";
import { useAiconGameGroupTableColumns } from "@/module/service-configs-module/aicon-game-group-module/aicon-game-group-table-columns";
import { ResultDtoLong, SysCfgRegularDto } from "@/shared/api";

export type AiconGameGroup = SysCfgRegularDto & {
  //Analog "extneds" in interfaces
  name: string;
  auth_groups: Set<string>;
  enabled: boolean;
  data: {
    perms: {
      room_id: string;
      partner_room_id: string;
      ai_room_id: string;
      game_types: string[];
    }[];
    tg_link?: string;
    expired_at?: number; //unix timestamp
    ai_subject?: string;
    token?: string; //Token is generated on BE, so we dont need to pass it on FE
  };
};

export function AiconGameGroupTable({ room }: { room: string }) {
  const { t } = useTranslation();
  const isOwner = useIsOwnerOrSuperAdmin();

  const [modalInitialValues, setModalInitialValues] = useState<AiconGameGroup | undefined>(
    undefined
  );

  //const [addConfig, setAddConfig] = useState<boolean>(false);
  const [isCreateEditModalOpen, setIsCreateEditModalOpen] = useState<boolean>(false);
  const [createEditGameGroupModalStatus, setCreateEditGameGroupModalStatus] = useState<
    "closed" | "edit" | "add"
  >("closed");
  const [bulkOperationResult, setBulkOperationResult] = useState<ResultDtoLong[] | undefined>(
    undefined
  );

  const {
    data: gameGroupsResponse,
    isLoading,
    refetch,
  } = useSysCfgQuery({
    domain: room,
    config_name: ROOM_CONFIGS_NAMES.GAME_GROUP,
  });

  const gameGroups = useMemo(() => {
    return (gameGroupsResponse?.data.content as AiconGameGroup[]) ?? [];
  }, [gameGroupsResponse]);

  /*const gameGroupsDisplayable = useMemo(()=> {
    const expandedGroups: AiconGameGroup[] = [];

    for (const group of gameGroups) {
      // Если нет perms или пустой массив, добавляем оригинальный объект
      if (!group.data.perms || group.data.perms.length === 0) {
        expandedGroups.push({...group});
        continue;
      }

      // Для каждого элемента в perms создаем новый объект
      for (const perm of group.data.perms) {
        // Создаем глубокую копию объекта
        const expandedGroup: AiconGameGroup = {
          ...group,
          auth_groups: new Set(group.auth_groups),
          data: {
            ...group.data,
            perms: [perm] // Оставляем только один perm
          }
        };

        expandedGroups.push(expandedGroup);
      }
    }

    return expandedGroups;
  }, [gameGroups])*/

  const cfgPerm = useCfgPerm({
    roomName: room,
    cfgName: ROOM_CONFIGS_NAMES.GAME_GROUP, //TODO to change
  });

  const { saveConfigMutation: saveConfig, isPending: isSaveConfigPending } = useSaveConfig({
    roomName: room,
    cfgName: ROOM_CONFIGS_NAMES.GAME_GROUP,
    onSuccessCallback: () => {
      void refetch();
      setCreateEditGameGroupModalStatus("closed");
      setModalInitialValues(undefined);
    },
  });

  const { updateConfigMutation: updateConfig, isPending: isUpdateConfigPending } =
    useUpdateConfig({
      roomName: room,
      cfgName: ROOM_CONFIGS_NAMES.GAME_GROUP,
      onSuccessCallback: () => {
        void refetch();
        setCreateEditGameGroupModalStatus("closed");
        setModalInitialValues(undefined);
      },
    });

  const handleChangeEnableBulk = useConfigChangeEnabled({
    roomName: room,
    cfgName: ROOM_CONFIGS_NAMES.GAME_GROUP,
    onSuccessCallback: (table) => {
      void refetch();
      table.resetRowSelection();
    },
  });

  const handleDeleteConfigs = useDeleteConfigs({
    roomName: room,
    cfgName: ROOM_CONFIGS_NAMES.GAME_GROUP,
    onSuccessCallback: (table, response) => {
      void refetch();
      setBulkOperationResult([response.data]);
      table.resetRowSelection();
    },
  });

  const { defaultProps, state, columnFilters, grouping, globalFilter } =
    useTableSettings<AiconGameGroup>("aicon.game_group");

  const table = useMaterialReactTable({
    data: /*gameGroupsDisplayable*/ gameGroups ?? [],
    columns: useAiconGameGroupTableColumns(),
    getRowId: (originalRow) => originalRow.id,
    selectAllMode: "all",
    state: {
      isLoading: isLoading,
      ...state,
      sorting: state.sorting.length > 0 ? state.sorting : [{ id: "name", desc: false }],
    },
    ...defaultProps,
    initialState: {
      pagination: { pageSize: 100, pageIndex: 0 },
      density: "compact",
      sorting: [{ id: "name", desc: false }],
    },
    enableRowActions: true,
    enableSelectAll: true,
    enableRowSelection: true,
    enableGrouping: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnFilterModes: true,
    enablePagination: true,
    enableSorting: true,
    muiTableHeadCellProps: {
      sx: {
        verticalAlign: "middle",
        "& *": {
          whiteSpace: "normal",
          lineHeight: 1.35,
        },
      },
    },
    defaultColumn: {
      enableResizing: false,
      grow: false,
      maxSize: 30,
      minSize: 10,
    },

    renderTopToolbarCustomActions: ({ table }) => {
      const selectedCount = table.getSelectedRowModel().rows.length;

      return (
        <div style={{ height: "40px", display: "flex", alignItems: "center" }}>
          {cfgPerm.write && (
            <Button onClick={() => setCreateEditGameGroupModalStatus("add")}>
              <AddOutlined /> {t("new")}
            </Button>
          )}

          {selectedCount > 0 && (
            <div style={{ marginLeft: 30, display: "flex" }}>
              {cfgPerm.execute && (
                <IconButton
                  size="small"
                  style={{ fontSize: "15px" }}
                  color="success"
                  onClick={() => handleChangeEnableBulk(table, true)}
                >
                  <Power /> {t("enable")}
                </IconButton>
              )}
              {cfgPerm.execute && (
                <IconButton
                  size="small"
                  style={{ fontSize: "15px" }}
                  color="error"
                  onClick={() => handleChangeEnableBulk(table, false)}
                >
                  <PowerOff /> {t("disable")}
                </IconButton>
              )}

              {cfgPerm.delete && (
                <Popconfirm
                  title={t("deleteItems")}
                  description={t("areYouSureToDeleteItems")}
                  onConfirm={() => handleDeleteConfigs(table)}
                >
                  <IconButton size="small" style={{ fontSize: "15px" }} color="error">
                    <DeleteOutlineOutlined /> {t("delete")}
                  </IconButton>
                </Popconfirm>
              )}

              {isOwner && (
                <ApplyAuthGroupsTableControl
                  domain="aicon"
                  cfgName={ROOM_CONFIGS_NAMES.GAME_GROUP}
                  table={table}
                  filters={assembleNamesFilter(table)}
                  totalElementsCount={selectedCount}
                  onSuccess={() => void refetch()}
                />
              )}
            </div>
          )}
        </div>
      );
    },
    renderRowActions: ({ row }) => (
      <Box>
        <IconButton
          onClick={() => {
            const gameGroupToEdit =
              gameGroups?.find((item) => item.id === row.original.id) ?? undefined;
            setCreateEditGameGroupModalStatus("edit");
            /* setModalInitialValues({
              //...row.original,
              ...gameGroupToEdit
              //auth_groups: row.original.auth_groups ?? new Set(),
            });*/
            setModalInitialValues(gameGroupToEdit);
          }}
        >
          <Edit />
        </IconButton>

        {/*{cfgPerm.write && (
          <IconButton
            onClick={() => {
              setModalInitialValues({ ...row.original, name: "" });
            }}
          >
            <ContentCopy />
          </IconButton>
        )}*/}
      </Box>
    ),
  });

  useEffect(() => {
    table.resetRowSelection();
  }, [columnFilters, grouping, globalFilter, table]);

  function onCloseModal() {
    setModalInitialValues(undefined);
    setCreateEditGameGroupModalStatus("closed");
  }

  return (
    <>
      <MaterialReactTable table={table} />

      {createEditGameGroupModalStatus === "add" && (
        <AiconGameGroupCreateEditModal
          mode={createEditGameGroupModalStatus}
          domain={room}
          onSave={(config: AiconGameGroup) => {
            saveConfig(config);
          }}
          initialData={modalInitialValues}
          onCancel={() => onCloseModal()}
        />
      )}

      {createEditGameGroupModalStatus === "edit" && (
        <AiconGameGroupCreateEditModal
          mode={createEditGameGroupModalStatus}
          domain={room}
          initialData={modalInitialValues}
          onSave={(config: AiconGameGroup) => {
            updateConfig(config.name, config);
          }}
          onHandsLimitChange={() => {
            void refetch();
          }}
          onCancel={() => onCloseModal()}
        />
      )}

      {bulkOperationResult && (
        <BulkOperationReportModal
          resultList={bulkOperationResult}
          title={t("operationResult")}
          showProblematic
          showSuccess
          onCancel={() => setBulkOperationResult(undefined)}
          open={!!bulkOperationResult}
        />
      )}
    </>
  );
}
